import React, { memo } from 'react';
import TimeSlotRow from './TimeSlotRow';

/**
 * SettledBox — Shows the last 2 completed slots.
 *
 * Each settled slot displays:
 *   - The original TimeSlotRow — exactly as the bet was placed
 *   - Close price + price delta
 *   - WIN or LOSS badge with the net amount
 */
const SettledBox = memo(({
    coin,
    historyItems,
    bets,
    settlements = [],
    getSentimentRatio,
    getPayoutRatio,
    handleDotClick,
    handleBetChange,
    submitBet,
    setBets
}) => {
    // Calculate the sequence of the last 3 settled minutes
    const now = new Date();
    // Round down to the current minute
    const currentMinuteMs = Math.floor(now.getTime() / 60000) * 60000;
    
    const recentHistory = [];
    for (let i = 3; i >= 1; i--) {
        const slotTimeMs = currentMinuteMs - (i * 60000);
        const d = new Date(slotTimeMs);
        const timeStr = `${d.getHours() % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')}`;
        
        // Find if we have history data for this specific minute
        const h = (historyItems || []).find(item => item.time === timeStr);
        if (h) {
            recentHistory.push({ ...h, slotTimeMs }); // attach ms for easier matching
        } else {
            recentHistory.push({ time: timeStr, start: 0, end: 0, _placeholder: true, slotTimeMs });
        }
    }

    return (
        <div className="flex flex-col">
            {recentHistory.map((item, index) => {
                const matchedBet = findMatchingBet(coin.id, item.time, bets);
                const matchedSlot = findMatchingSlot(coin.id, item.time, bets);
                const isUp = item.end > item.start;
                const isPush = item.end === item.start;
                const priceDiff = item.end - item.start;

                // Get bet direction + amount from local bet or server settlement
                let betDirection = null;
                let betAmount = 0;
                let serverNet = null;

                let serverResult = null;

                if (matchedBet) {
                    betDirection = matchedBet.direction;
                    betAmount = parseFloat(matchedBet.amount || 0);
                } else {
                    const settlement = findMatchingSettlement(item.time, settlements, item.slotTimeMs);
                    if (settlement) {
                        betDirection = settlement.direction;
                        // Server values are in SOL, convert to credits (×100)
                        betAmount = settlement.amount * 100;
                        serverNet = settlement.net * 100;
                        serverResult = settlement.result;
                    }
                }

                let winLoss = null;
                if (betDirection) {
                    let isWinFinal = false;
                    let isPushFinal = false;

                    if (serverResult) {
                        isWinFinal = serverResult === 'WIN';
                        isPushFinal = serverResult === 'PUSH';
                    } else {
                        isPushFinal = isPush;
                        isWinFinal = !isPushFinal && (
                            (isUp && betDirection === 'UP') ||
                            (!isUp && betDirection === 'DOWN')
                        );
                    }

                    winLoss = {
                        isWin: isWinFinal,
                        isPush: isPushFinal,
                        direction: betDirection,
                        amount: betAmount,
                        net: serverNet !== null ? serverNet :
                            (isPushFinal ? 0 : (isWinFinal ? betAmount * 0.97 : -betAmount))
                    };
                }

                // Placeholder row — no history data yet, but still render price row
                if (item._placeholder) {
                    return (
                        <React.Fragment key={`settled_${item.time}_${index}`}>
                            {/* Price row — always visible, shows dashes when no server data */}
                            <div className="relative flex items-center px-3 h-[25px] border-b border-white/5 bg-gray-900/30">
                                <span className="font-mono text-sm font-normal text-white/20 tracking-tight">— / —</span>
                            </div>
                            <TimeSlotRow
                                coinId={coin.id}
                                slot={{ id: `placeholder_${index}`, time: item.time }}
                                isNextFive={false}
                                isLocked={true}
                                isSettled={true}
                                isLastSettled={index === recentHistory.length - 1}
                                winLoss={null}
                                betState={undefined}
                                getSentimentRatio={() => '—'}
                                getPayoutRatio={() => '—'}
                                handleDotClick={handleDotClick}
                                handleBetChange={handleBetChange}
                                submitBet={submitBet}
                                setBets={setBets}
                                poolTotal={item.totalPool || 0}
                            />
                        </React.Fragment>
                    );
                }

                return (
                    <React.Fragment key={`settled_${item.time}_${index}`}>
                        {/* Price result line — always visible regardless of bet */}
                        <div className={`relative flex items-center px-3 h-[25px] border-b border-white/5 ${
                            winLoss?.direction === 'UP' ? 'bg-green-500/10' :
                            winLoss?.direction === 'DOWN' ? 'bg-red-500/10' :
                            'bg-gray-900/30'
                        }`}>
                            {/* Open price (blue) */}
                            {item.start > 0 ? (
                                <span className="font-mono text-sm font-normal text-blue-400 tracking-tight">
                                    {item.start.toFixed(2)}
                                </span>
                            ) : (
                                <span className="font-mono text-sm font-normal text-white/20">—</span>
                            )}
                            <span className="font-mono text-sm font-normal text-white/40 mx-1">/</span>
                            {/* Close price (white) */}
                            {item.end > 0 ? (
                                <span className="font-mono text-sm font-normal text-white tracking-tight">
                                    {item.end.toFixed(2)}
                                </span>
                            ) : (
                                <span className="font-mono text-sm font-normal text-white/20">—</span>
                            )}
                            {/* Price delta */}
                            {item.start > 0 && item.end > 0 && (
                                <span className={`font-mono text-xs font-normal ml-2 ${
                                    isUp ? 'text-green-400' : isPush ? 'text-white/40' : 'text-red-400'
                                }`}>
                                    ({priceDiff >= 0 ? '+' : ''}{priceDiff.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                                </span>
                            )}
                            {/* WIN/LOSS/PUSH badge — only if player bet */}
                            {winLoss && (
                                <>
                                    <span className={`ml-auto mr-1 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${
                                        winLoss.isPush ? 'bg-white/10 text-white/60' :
                                        winLoss.isWin ? 'bg-green-500/20 text-green-400' :
                                        'bg-red-500/20 text-red-400'
                                    }`}>
                                        {winLoss.isPush ? 'PUSH' : winLoss.isWin ? 'WIN' : 'LOSS'}
                                    </span>
                                    <span className={`font-mono text-sm font-bold tracking-tight ${
                                        winLoss.isPush ? 'text-white/60' :
                                        winLoss.isWin ? 'text-green-400' :
                                        'text-red-400'
                                    }`}>
                                        {winLoss.isPush ? '0' : winLoss.isWin
                                            ? '+' + parseFloat(Math.abs(winLoss.net).toFixed(2))
                                            : '-' + Math.round(Math.abs(winLoss.net))}
                                    </span>
                                </>
                            )}
                        </div>

                        {/* Original TimeSlotRow — exactly as placed */}
                        <TimeSlotRow
                            coinId={coin.id}
                            slot={matchedSlot || { id: `settled_${item.time}`, time: item.time }}
                            isNextFive={false}
                            isLocked={true}
                            isSettled={true}
                            isLastSettled={index === recentHistory.length - 1}
                            winLoss={winLoss}
                            betState={matchedBet || (winLoss ? { direction: winLoss.direction, amount: winLoss.amount, locked: true } : undefined)}
                            getSentimentRatio={item.upPct !== undefined
                                ? () => `${item.upPct}/${100 - item.upPct}`
                                : getSentimentRatio}
                            getPayoutRatio={item.totalPool !== undefined
                                ? (_, __, dir) => {
                                    const side = dir === 'UP' ? (item.totalUp || 0) : (item.totalDown || 0);
                                    if (side <= 0) return '2.00x';
                                    return ((item.totalPool * 0.97) / side).toFixed(2) + 'x';
                                }
                                : getPayoutRatio}
                            handleDotClick={handleDotClick}
                            handleBetChange={handleBetChange}
                            submitBet={submitBet}
                            setBets={setBets}
                            poolTotal={(item.totalPool || (item.totalUp || 0) + (item.totalDown || 0)) || 0}
                        />
                    </React.Fragment>
                );
            })}
        </div>
    );
});

/**
 * Find a matching bet for a settled time slot.
 */
function findMatchingBet(coinId, timeStr, bets) {
    const match = Object.entries(bets).find(([key, val]) => {
        if (!key.startsWith(coinId) || !val.locked) return false;
        const ts = parseInt(key.split('_slot_')[1]);
        if (!ts) return false;
        const d = new Date(ts);
        const display = `${d.getHours() % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')}`;
        return display === timeStr;
    });
    return match ? match[1] : null;
}

/**
 * Find the matching slot object (with id and time) for a settled time.
 */
function findMatchingSlot(coinId, timeStr, bets) {
    const match = Object.entries(bets).find(([key, val]) => {
        if (!key.startsWith(coinId) || !val.locked) return false;
        const ts = parseInt(key.split('_slot_')[1]);
        if (!ts) return false;
        const d = new Date(ts);
        const display = `${d.getHours() % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')}`;
        return display === timeStr;
    });
    if (match) {
        const ts = parseInt(match[0].split('_slot_')[1]);
        return { id: `slot_${ts}`, time: timeStr };
    }
    return null;
}

/**
 * Find a matching server settlement for a settled time slot.
 * Uses displayTime (local timezone) or falls back to parsing time string.
 */
function findMatchingSettlement(timeStr, settlements, slotTimeMs) {
    if (!settlements || settlements.length === 0) return null;
    // Try matching by slotTimeMs first (most reliable)
    if (slotTimeMs) {
        const msMatch = settlements.find(s => s.slotTimeMs === slotTimeMs);
        if (msMatch) return msMatch;
    }
    // Then try displayTime (local timezone formatted like "1:05")
    const displayMatch = settlements.find(s => s.displayTime === timeStr);
    if (displayMatch) return displayMatch;
    // Fallback: parse the locale string
    return settlements.find(s => {
        try {
            const d = new Date(s.time);
            const display = `${d.getHours() % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')}`;
            return display === timeStr;
        } catch {
            return false;
        }
    }) || null;
}

export default SettledBox;
